import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { useGlobalHomeSelectStore } from '@/features/shop/components/GlobalHomePage/useGlobalHomeSelectStore'
import { Box, Column, Row, Stack } from '@shipt/design-system-layouts'
import { Body, Headline } from '@shipt/design-system-typography'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import styled from 'styled-components'
import { TransparentAnchor } from '@/features/shared/elements/Buttons'
import { getRetailerUrl } from '@shared/constants/routes'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { CartFillIcon, ChevronRightIcon } from '@shipt/design-system-icons'
import { screenSizes } from '@shipt/design-system-themes'
import {
  trackAvailableStoreClicked,
  trackAvailableStoreViewed,
} from '@/features/shared/analytics/selectStore'
import { type StoreCard as StoreCardType } from '@/features/cms/components/CMS/types'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { type StoreCardsRenderData } from '@/features/cms/components/CMS/components/StoresCarousel'
import { StoreAttribute } from '@/features/cms/components/CMS/components/BrandAmplification/StoreAttribute'
import { getHeadingElement } from '@/features/shared/utils/getHeadingElement'
import { useQueryAllShoppingCarts } from '@/features/shop/services/Carts/queries'
import { theme } from '@/features/shared/constants/shiptTheme'

export const DetailedStoresCarousel = ({
  content_type_id,
  id,
  deliveryStoresList,
  heading,
  analyticSlug,
  seoHeadLevel,
}: {
  heading?: string
  deliveryStoresList: StoreCardsRenderData[]
  content_type_id: string
  id: string
  analyticSlug?: string
  seoHeadLevel: number
}) => {
  const { selectStore } = useGlobalHomeSelectStore()

  const { data: allCarts } = useQueryAllShoppingCarts()
  const cartCountByLocationId = allCarts?.reduce<Record<number, number>>(
    (acc, { cart, context }) => {
      if (cart.num_items > 0) acc[context.store_location_id] = cart.num_items
      return acc
    },
    {},
  )

  const getStoreTrackingProps = (
    store: StoreCardsRenderData,
  ): Omit<Parameters<typeof trackAvailableStoreViewed>[0], 'index'> => {
    const { primary_attributes, secondary_attributes } = store
    const isNew = store.recently_launched
    const hasCartInProgress = !!cartCountByLocationId?.[store.store_location_id]
    // Badges are mutually exclusive in the UI (New takes priority over cart),
    // so the visible `attributes` array shows the cart badge only when not new.
    const showCartBadge = !isNew && hasCartInProgress
    const visibleBadgeTags = [
      ...(isNew ? ['new'] : []),
      ...(showCartBadge ? ['cart_in_progress'] : []),
    ]
    // `available_attributes` captures both flags for measurement.
    const availableBadgeTags = [
      ...(isNew ? ['new'] : []),
      ...(hasCartInProgress ? ['cart_in_progress'] : []),
    ]
    const combinedAttributesText = [
      ...(primary_attributes ?? []),
      ...(secondary_attributes ?? []),
    ].map((attribute) => attribute.data.text)
    const promoIds = primary_attributes?.flatMap(
      (attribute) =>
        attribute.metadata?.read_operation_info.source_object.object.promotion_id ?? [],
    )
    return {
      displaySublocation: analyticSlug,
      storeId: store.retailer_id,
      storeLocationId: store.store_location_id,
      storeName: store.name,
      isNew: store.recently_launched,
      attributes: [...visibleBadgeTags, ...combinedAttributesText],
      availableAttributes: [...availableBadgeTags, ...combinedAttributesText],
      promos: promoIds,
    }
  }

  const handleClick = (store: StoreCardsRenderData, idx: number) => {
    trackAvailableStoreClicked({
      index: idx,
      ...getStoreTrackingProps(store),
    })
    selectStore({
      ...store,
      id: store.retailer_id,
      is_pickup: !!store.pickup_eligible,
    })
  }

  const handleInView = (store: StoreCardsRenderData, index: number) => {
    trackAvailableStoreViewed({
      index,
      ...getStoreTrackingProps(store),
    })
  }

  const storeDetailsGetter = (store: StoreCardsRenderData) => {
    return (
      store.secondary_attributes
        ?.filter((attribute) => attribute.data.treatment === 'Subtle')
        .map((attribute) => attribute.data.text) ?? []
    )
  }

  return (
    <Container id="bottom-stores-carousel">
      <GridLayoutContainer {...getCMSWrapperProps({ id, content_type_id })}>
        {!!heading && (
          <Box marginBottom="md">
            <Headline as={getHeadingElement(seoHeadLevel)}>{heading}</Headline>
          </Box>
        )}
        <Mobile className="fullWidth">
          {deliveryStoresList.map((store, index) => (
            <StoreCard
              storeDetails={storeDetailsGetter(store)}
              size="md"
              href={getRetailerUrl(store.name)}
              onClick={(e) => {
                e.preventDefault()
                handleClick(store, index)
              }}
              key={store.retailer_id}
              store={store}
              onInView={() => handleInView(store, index)}
              cartItemCount={cartCountByLocationId?.[store.store_location_id]}
            />
          ))}
        </Mobile>
        <Desktop>
          {deliveryStoresList.map((store, index) => (
            <StoreCard
              size="lg"
              storeDetails={storeDetailsGetter(store)}
              href={getRetailerUrl(store.name)}
              onClick={(e) => {
                e.preventDefault()
                handleClick(store, index)
              }}
              key={store.retailer_id}
              store={store}
              onInView={() => handleInView(store, index)}
              cartItemCount={cartCountByLocationId?.[store.store_location_id]}
            />
          ))}
        </Desktop>
      </GridLayoutContainer>
    </Container>
  )
}

const StoreCard = ({
  onClick,
  href,
  store,
  storeDetails,
  onInView,
  size,
  cartItemCount,
}: {
  onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  href: string
  store: StoreCardType['data']
  storeDetails: string[]
  onInView: () => void
  size: 'md' | 'lg'
  cartItemCount?: number
}) => {
  return (
    <StoreCardContainer
      href={href}
      aria-describedby={`store-details-${store.retailer_id}`}
      aria-label={`select ${store.name} as your store`}
      onClick={onClick}
    >
      <Row align="center" justify="space-between">
        <StoreContent
          size={size}
          store={store}
          storeDetails={storeDetails}
          onInView={onInView}
          cartItemCount={cartItemCount}
        />
        {size === 'md' && <ChevronRightIcon />}
      </Row>
      <Divider />
    </StoreCardContainer>
  )
}

const StoreContent = ({
  store,
  storeDetails,
  size,
  onInView,
  cartItemCount,
}: {
  store: StoreCardType['data']
  storeDetails: string[]
  size: 'md' | 'lg'
  onInView: () => void
  cartItemCount?: number
}) => {
  const { primary_attributes } = store
  const primaryLoudPromo = primary_attributes?.find(
    (attr) => attr.data.treatment === 'Primary Loud',
  )

  const secondaryLoud = primary_attributes?.find((attr) => attr.data.treatment === 'Secondary Loud')

  return (
    <ScrollIntoView onInView={onInView}>
      <Row align="center" spacing="md" padding={{ base: [0, 'lg'], md: [0] }}>
        <StoreTile backgroundColor={store.background_color}>
          <StoreLogo
            alt={`${store.name} store image`}
            src={store.logo.src}
            width={size === 'md' ? 56 : 89}
            height={size === 'md' ? 40 : 64}
            fallbackType="store"
          />
        </StoreTile>
        <StoreTextWrapper align="start">
          <Row align="center" spacing="sm">
            <StoreName strong lineHeight="compact" size={{ base: 'md', md: 'lg' }}>
              {store.name}
            </StoreName>

            {!!store.recently_launched && (
              <NewBadgeStyles surface="inverse" size="sm" strong lineHeight="compact">
                New
              </NewBadgeStyles>
            )}
            {!!cartItemCount && !store.recently_launched && (
              <BadgeWrapper>
                <CartFillIcon size="sm" />
                <Body size="sm" strong>
                  {cartItemCount}
                </Body>
              </BadgeWrapper>
            )}
          </Row>
          {storeDetails.length > 0 && (
            <Stack id={`store-details-${store.retailer_id}`} direction="column">
              <Row wrap>
                {storeDetails.map((detail, idx) => (
                  <StoreDetails key={detail} size={{ base: 'sm', md: 'md' }} variant="secondary">
                    {`${detail}${idx !== storeDetails.length - 1 ? ' \u2027 ' : ''}`}
                  </StoreDetails>
                ))}
              </Row>
            </Stack>
          )}
          {!!(primaryLoudPromo || secondaryLoud) && (
            <Row align="center" spacing="xs">
              {!!primaryLoudPromo && <StoreAttribute isDetailed {...primaryLoudPromo.data} />}
              {!!primaryLoudPromo && !!secondaryLoud && ' \u2027 '}
              {!!secondaryLoud?.data && <StoreAttribute isDetailed {...secondaryLoud.data} />}
            </Row>
          )}
        </StoreTextWrapper>
      </Row>
    </ScrollIntoView>
  )
}

const Container = styled.div`
  container-type: inline-size;
`

const NewBadgeStyles = styled(Body)`
  background-color: rgb(0 0 0 / 70%);
  border-radius: 0.25rem;
  padding: 0.125rem;
`

const StoreCardContainer = styled(TransparentAnchor)`
  position: relative;
  overflow: visible;
  width: 100%;
  padding: 1rem 1rem 0 0;

  &:hover,
  &:hover::before {
    background-color: ${theme.color.background.base.neutral.subtle_100};
  }

  &:active,
  &:active::before {
    background-color: ${theme.color.background.base.neutral.soft};
  }

  @media ${screenSizes.tablet} {
    border-radius: 8px;
    padding: 1rem 0 0;

    &::before {
      content: '';
      position: absolute;
      inset: 0 0 0 -1rem;
      border-radius: 8px;
      z-index: -1;
    }

    &:focus-visible {
      outline: solid 2px ${theme.color.focus.default};
      outline-offset: 2px;
    }
  }
`

const Divider = styled.span`
  display: flex;
  align-self: stretch;
  height: 1px;
  background-color: ${theme.color.border.base.neutral.subtle};
  margin: 1rem 0.4rem 0 1rem;

  @media ${screenSizes.tablet} {
    margin: 1rem 1rem 0 0;
  }

  ${StoreCardContainer}:last-of-type & {
    background-color: transparent;
  }
`

const StoreTextWrapper = styled(Column)`
  overflow-x: hidden;

  @media ${screenSizes.tablet} {
    width: calc(100% - 120px);
  }
`

const StoreDetails = styled(Body)`
  padding-right: 0.25rem;
  text-wrap: nowrap;
`

const StoreTile = styled.div<{ backgroundColor?: string }>`
  border-radius: 8px;
  background-color: ${({ backgroundColor }) => `${backgroundColor}`};

  display: flex;
  position: relative;
  border: 1px solid rgb(0 0 0 / 10%);
  flex-shrink: 0;
`

const StoreLogo = styled(Image)`
  object-fit: contain;
`

const Mobile = styled(Column)`
  @container ${screenSizes.tablet} {
    display: none;
  }
`
const Desktop = styled.div`
  display: none;

  @container ${screenSizes.tablet} {
    display: grid;
    column-gap: 12px;

    > * ${StoreCardContainer}::before {
      left: -1rem;
      right: 0;
    }
  }

  @container ${screenSizes.only_tablet} {
    grid-template-columns: repeat(2, minmax(50%, 1fr));

    > *:nth-child(2n + 1):nth-last-child(-n + 2),
    > *:nth-child(2n + 1):nth-last-child(-n + 2) ~ * {
      padding-bottom: 1rem;

      ${Divider} {
        background-color: transparent;
      }
    }
  }

  @container ${screenSizes.smDesktop} {
    grid-template-columns: repeat(3, minmax(33%, 1fr));

    > *:nth-child(3n + 1):nth-last-child(-n + 3),
    > *:nth-child(3n + 1):nth-last-child(-n + 3) ~ * {
      padding-bottom: 1rem;

      ${Divider} {
        background-color: transparent;
      }
    }
  }
`

const StoreName = styled(Body)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BadgeWrapper = styled.div`
  display: flex;
  gap: 0.25rem;
`
